import { NOTES_TURQUOISE_STOPS } from './notesStyles';

import type { CSSProperties } from 'react';

import { useAmelioTranslations } from '@/hooks';

/** Thin diagonal rules every 41px, drawn in the layer's text colour so no white is written here. */
const DIAGONAL_RULES: CSSProperties = {
    backgroundImage:
        'repeating-linear-gradient(135deg, transparent 0 40px, currentColor 40px 41px)',
};

interface ManagerNotesCardProps {
    employeeFirstName: string;
    dataTestId?: string;
}

/**
 * The intro card of the "Notes" tab: what the notes are for and who reads them, centred on a
 * tall turquoise card whose decoration (two soft discs, a deep accent disc, diagonal rules and
 * a blurred glow) carries no meaning and stays out of the accessibility tree.
 */
export const ManagerNotesCard = ({
    employeeFirstName,
    dataTestId = 'manager-notes-card',
}: ManagerNotesCardProps) => {
    const { t } = useAmelioTranslations();

    return (
        <section
            className={`${NOTES_TURQUOISE_STOPS} relative flex flex-col justify-center overflow-hidden rounded-[30px] bg-gradient-to-br px-6 py-8 text-white md:px-8 lg:min-h-[320px]`}
            data-testid={dataTestId}
        >
            <div
                aria-hidden={'true'}
                className={'pointer-events-none absolute inset-0'}
            >
                <div
                    className={
                        'absolute -right-[64px] -bottom-[64px] size-[280px] rounded-full bg-white/7'
                    }
                />
                <div
                    className={
                        'absolute -top-[40px] -right-[40px] size-[180px] rounded-full bg-white/5'
                    }
                />
                <div
                    className={
                        'bg-turquoise-deep/40 absolute bottom-[48px] left-[32px] size-[100px] rounded-full'
                    }
                />
                <div
                    className={'absolute inset-0 opacity-6'}
                    style={DIAGONAL_RULES}
                />
                <div
                    className={
                        'absolute top-1/2 left-1/3 size-[200px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/8 blur-3xl'
                    }
                />
            </div>

            <div className={'relative flex max-w-[290px] flex-col gap-4'}>
                <h3 className={'text-[24px] leading-tight font-medium'}>
                    {t('performanceProfile.notesTab.managerTitle')}
                </h3>
                <p className={'text-[14px] leading-[26px] font-medium text-white/90'}>
                    {t('performanceProfile.notesTab.managerDescription', {
                        name: employeeFirstName,
                    })}
                </p>
            </div>
        </section>
    );
};
